import { analytics, Sentry } from '@opendoor/observability';
import { ListenerEffectAPI } from '@reduxjs/toolkit';
import { LOCATION_CHANGE, push, replace } from 'connected-react-router';

import {
  addressVerifiedStatusEnumToAddressVerifiedStringUnion,
  channelStringToEnum,
  generateDenialReasonDisplayString,
  getOpendoorDirectDenialReason,
  getProductOfferings,
} from 'src/api/sellerInputToLead';
import { ProductOffering } from 'src/declarations';
import { leadQualifyConversionEvent } from 'src/helpers/eventTracking';
import { getPageSet } from 'src/helpers/flows/pageSets';
import { showPrompt } from 'src/helpers/showPrompt';
import { isOpendoorAppEvoWebView, postDataToWebview } from 'src/helpers/webview';
import { getPageLoader, getPageLoaderSlugs } from 'src/lib/flow/loaders';
import {
  getEligibleProducts,
  getHasHomebuilderSalesAssociate,
  getHomeBuilderIdentifier,
  getHomeValuationEstimate,
  getIsInPartnershipExpansionDenial,
  getLeadState,
  getOrePartnerIdentifier,
  isInBwODMarket,
} from 'src/lib/flow/sellerInputUtils';
import { sellerFlowPagePath, sellerGlobalPagePath } from 'src/routes/pathBuilders';

import { getInitialSellerFlowName } from '../helpers/sellerFlow';
import {
  createNewFlowState,
  FlowContext,
  FlowState,
  FlowUiState,
  getPageSubmit,
  getStateFromSlug,
  getUpdatedFlowState,
  initFlowState,
  nextPage,
  previousPage,
} from '../lib/flow';
import { api } from './api';
import { loadExperiment } from './apiUtils';
import { ExperimentFlag } from './experimentFlags';
import { atStart, updateFlow, updateIndex, updateInputContext } from './flow_ng';
import { AppDispatch, RootState } from './index';
import { startAppListening } from './middleware';
import { getInApp } from './router/selectors';
import {
  areAllProductsDenied,
  changedAnswers,
  denialReason,
  getAnswers,
  isInExclusiveOffersTreatment,
  isPartnerFlow,
} from './sellerFlow/selectors';
import {
  changeAnswers,
  SavedAnswer,
  SellerInput,
  updateSellerInputAfterUpdateAnswers,
} from './sellerInput';
import {
  endPageLoading,
  flowInitialized,
  submitAndGoToNextPage,
  goToNextPage as uiGoToNextPage,
  submitAnswers as uiSubmitAnswers,
  updateUi,
} from './ui';

const getInputContextFromSellerInput = (input: SellerInput) => {
  // TODO: we have a lot of this code duplicated below in the getSellerInput fufilled listener
  // we need to do this also on modify_address and updateAnswers, so we should make all three use the same code path

  const eligibleProducts = input.productOfferings
    .filter((productOffering) => !productOffering.denied)
    .map((productOffering) => productOffering.product);
  // HACK: Leads created in the seller app use Web and not Reception, meaning they'll never
  // have any product offerings at all.
  // We should remove this once the mobile app seller flow is moved onto Reception.
  if (input.productOfferings.length === 0 && input.leadState !== 'denied') {
    eligibleProducts.push('opendoor_direct');
  }

  return {
    answerPrefills: input.answerPrefills,
    addressUUID: input.address.uuid,
    sellerInputUUID: input.leadToken,
    channel: input.channel,
    marketDisplayName: input.marketDisplayName,
    market: input.market,
    isInBwODMarket: isInBwODMarket(input.market),
    eligibleProducts,
    partnerships: input.partnerships,
    homebuilderIdentifier: getHomeBuilderIdentifier(input.partnerships),
    orePartnerIdentifier: getOrePartnerIdentifier(input.partnerships),
    isInPartnershipExpansionDenial: getIsInPartnershipExpansionDenial(
      input.productOfferings,
      input.denialPartnerIdentifier,
    ),
    hasHomebuilderSalesAssociate: input.hasHomebuilderSalesAssociate,
    inApp: isOpendoorAppEvoWebView, // need to check queryparam - see getInApp
    sellerFlowName: input.sellerFlowName,

    // this needs to be a combo of homebuilderIdentifier and seller
    // answers, so maybe it shouldn't be here..
    isHomebuilderLead: false,
    // this also depends on answers..
    // these could be helpers the flow lib has that are like isOpcityEligible(flowContext)
    //opcityEligible?: boolean;
    homeValuationEstimate:
      input.homeEstimateMin &&
      input.homeEstimateMax &&
      typeof input.homeEstimateVariance !== 'undefined'
        ? {
            min: input.homeEstimateMin,
            max: input.homeEstimateMax,
            variance: input.homeEstimateVariance,
          }
        : undefined,
  };
};

export const getFlowFromState = (
  appState: RootState,
): { state: FlowState; context: FlowContext } => {
  const { index, atEnd, exitPath, atStart, lastCompletedIndex } = appState.flow_ng;
  // need to properly handle this
  const ui = appState.ui;

  const answers = getAnswers(appState);

  const context: FlowContext = {
    answers,
    inputContext: appState.flow_ng.inputContext,
    customer: {
      // might change during app usage?
      customerUUID: appState.sellerInput.customerUUID,
      yourOrYourClients: 'your',
      yourOrYourClient: 'your',
    },
    featureFlags: appState.featureFlags,
    experimentFlags: appState.experimentFlags,
  };

  const { pageSet } = getPageSet(appState.flow_ng.pageSetName, context);

  const state: FlowState = {
    index,
    lastCompletedIndex,
    atEnd,
    atStart,
    exitPath,
    pageSet,
    ui: ui as FlowUiState,
  };

  return { state, context };
};

startAppListening({
  actionCreator: changeAnswers,
  effect: (_action, listenerApi) => {
    const appState = listenerApi.getState();
    const { state, context } = getFlowFromState(appState);
    const flowState = getUpdatedFlowState(state, context);

    const { index, atEnd, atStart, ui } = flowState;
    listenerApi.dispatch(
      updateFlow({
        index,
        atEnd,
        atStart,
        pageSetName: appState.flow_ng.pageSetName,
        lastCompletedIndex: index,
        inputContext: context.inputContext,
      }),
    );
    listenerApi.dispatch(updateUi(ui));

    const answers = changedAnswers(appState) ?? {};
    if (flowState.ui.isReadyForAutoSubmit && Object.keys(answers).length > 0) {
      listenerApi.dispatch(submitAndGoToNextPage());
    }
  },
});

// updateAnswers mutation will eventually update our product offerings
// these offerings affect the flow
// you can think of this as completed updateAnswers
startAppListening({
  actionCreator: updateSellerInputAfterUpdateAnswers,
  effect: async (_: Action, listenerApi: ListenerEffectAPI<RootState, AppDispatch>) => {
    const appState = listenerApi.getState();

    const { state, context } = getFlowFromState(appState);
    context.inputContext = getInputContextFromSellerInput(appState.sellerInput);
    const flowState = getUpdatedFlowState(state, context);
    listenerApi.dispatch(updateInputContext(context.inputContext));
    listenerApi.dispatch(updateUi(flowState.ui));
  },
});

const isOpenProductOfferingAndNotREIT = (offering: ProductOffering): boolean => {
  if (offering.denied || offering.qualified) {
    return false;
  }

  if (offering.product == 'reit') {
    // REIT productOfferings never set qualifed or denied, so we
    // essentially ignore REIT when deciding if a user should go through
    // onboarding or not
    //
    // Some additional context:
    // https://opendoor.slack.com/archives/C02TS8EQHC1/p1669752550763699
    // https://opendoor.slack.com/archives/C02H706BM8T/p1669741200754039
    return false;
  }

  return true;
};

// handle logic for start of flow, route user to correct path based
// on seller input state and/or available product offerings
const handleAtStart = async (_: Action, listenerApi: ListenerEffectAPI<RootState, AppDispatch>) => {
  const appState = listenerApi.getState();
  const { state, context } = getFlowFromState(appState);
  context.inputContext = {
    ...context.inputContext,
    ...getInputContextFromSellerInput(appState.sellerInput),
  };
  const startingSlug = state.ui.page.slug;

  const flowState = getUpdatedFlowState(state, context);
  listenerApi.dispatch(updateInputContext(context.inputContext));
  listenerApi.dispatch(updateUi(flowState.ui));
  const isDeniedForAllProducts = areAllProductsDenied(appState);
  const isExlusivesPassthrough = isInExclusiveOffersTreatment(appState);
  const isUntreatedRoomCondition = appState.flow_ng.pageSetName === 'untreated_room_condition';

  const orePartnerIdentifier = context.inputContext.orePartnerIdentifier;
  const leadState = appState.sellerInput.leadState;
  const leadToken = appState.sellerInput.leadToken;
  const inApp = getInApp(appState);
  const partnerFlow = isPartnerFlow(appState);
  const isAddressEntryConfirmationTreatment =
    appState.experimentFlags.addressEntryConfirmation === 'treatment';
  if (isUntreatedRoomCondition) {
    // Do not redirect anywhere at the start for untreated room condition
    return;
  }

  // for the partner flow, usually all products are denied in this case but
  // users can be sent to a 3rd party partner
  if (partnerFlow) {
    listenerApi.dispatch(
      replace(
        sellerFlowPagePath({
          token: leadToken,
          slug: startingSlug,
          inApp,
        }),
      ),
    );
  } else if (leadState === 'verifying_address' && !isAddressEntryConfirmationTreatment) {
    listenerApi.dispatch(
      replace(
        sellerGlobalPagePath({
          token: leadToken,
          page: 'address',
        }),
      ),
    );
  } else if (leadState === 'verifying_listing') {
    listenerApi.dispatch(
      replace(
        sellerGlobalPagePath({
          token: leadToken,
          page: 'verifying-listing',
        }),
      ),
    );
  } else if (leadState === 'verifying_address_manual') {
    listenerApi.dispatch(
      replace(
        sellerGlobalPagePath({
          token: leadToken,
          page: 'address-manual',
        }),
      ),
    );
  } else if ((leadState === 'denied' || isDeniedForAllProducts) && !isExlusivesPassthrough) {
    listenerApi.dispatch(
      replace(
        sellerGlobalPagePath({
          token: leadToken,
          page: 'unavailable',
        }),
      ),
    );
  } else if (
    !appState.sellerInput.changedAnswers['home.user_verified_address'] &&
    isAddressEntryConfirmationTreatment &&
    !isExlusivesPassthrough
  ) {
    listenerApi.dispatch(
      replace(
        sellerGlobalPagePath({
          token: leadToken,
          page: 'address-confirm',
        }),
      ),
    );
  } else if (
    orePartnerIdentifier ||
    // if seller has at least one product that is not in a terminal
    // state (qualified or denied) ignore REIT because there is a bug with REIT not\
    // reaching a terminal state after flow completion
    appState.sellerInput.productOfferings.some(isOpenProductOfferingAndNotREIT) ||
    isExlusivesPassthrough
  ) {
    listenerApi.dispatch(
      replace(
        sellerFlowPagePath({
          token: leadToken,
          slug: startingSlug,
          inApp,
        }),
      ),
    );
  } else {
    // seller should be fully qualified for some product by this point and they
    // should be sent to the hub
    listenerApi.dispatch(
      replace(
        sellerGlobalPagePath({
          token: leadToken,
          page: 'last-step',
        }),
      ),
    );
  }
};

startAppListening({
  actionCreator: atStart,
  effect: async (_: Action, listenerApi: ListenerEffectAPI<RootState, AppDispatch>) => {
    handleAtStart(_, listenerApi);
  },
});

const submitAnswers = async (
  action: ReturnType<typeof uiSubmitAnswers> | ReturnType<typeof submitAndGoToNextPage>,
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
) => {
  const appState = listenerApi.getState();

  let forceSubmit: Array<string> = [];
  if (action.payload?.additionalAnswerKeysToSubmit) {
    forceSubmit = action.payload.additionalAnswerKeysToSubmit;
  }

  const { state: preSubmitState, context: preSubmitContext } = getFlowFromState(appState);
  // for the current page get all our answers + things to clear
  const { submittedAnswers, completedSlug } = getPageSubmit(
    preSubmitState,
    preSubmitContext,
    preSubmitContext.answers,
  );

  forceSubmit.forEach((key) => {
    submittedAnswers[key] = preSubmitContext.answers[key];
  });

  // TODO: how should we handle the empty answers and no completedSlug case?
  // This happens when users go back and then forward on a page like overview or agent-education
  listenerApi.dispatch(
    api.endpoints.updateAnswers.initiate({
      uuid: appState.sellerInput.leadToken,
      completedSlug,
      submittedAnswers,
    }),
  );
};

function isFlowState(x: any): x is FlowState {
  return !!x?.ui;
}

/**
 * This function handles processing loaders, and if necessary advancing to the
 * next page in the flow.
 *
 * @param getNextPage - Can be either `nextPage` or `previousPage`,
 * this allows us to do loaders backwards for when a user has refreshed their
 * browser and then hits the back button
 *
 * @returns the new FlowState if any loaders were called, otherwise null
 */
const handleLoaders = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  getNextPage: typeof nextPage,
  startSlug: string,
  baseState: FlowState,
): Promise<{ flowState: FlowState | null; pageFound: boolean }> => {
  let loader = getPageLoader(startSlug);
  let limit = 10;
  // our next page might be a page with a loader, if it is we should loop,
  // call any loaders, and exit the loop when we have a page that isEnabled
  // or we are at the end of the flow
  //
  // We loop since one page might have a loader, we load that data, but the
  // page is still disabled (for example it's behind an experiment flag the
  // user is active for), and so we need to get the next page, which might have a
  // loader and so we start again...

  let alwaysReturnPagesWithLoaders = getPageLoaderSlugs().filter((slug) => slug != startSlug);
  let nextPageState: FlowState | null = null;
  let pageFound = true;
  while (loader) {
    const currentSlug = isFlowState(nextPageState) ? nextPageState.ui.page.slug : startSlug;
    await Promise.all(loader.map((l) => l(listenerApi)));

    const stateAfterLoader = listenerApi.getState();

    const { context: loaderContext } = getFlowFromState(stateAfterLoader);

    // re-try getting next page from our original state, but with our new context
    nextPageState = getNextPage(baseState, loaderContext, alwaysReturnPagesWithLoaders);

    // When there is a test on the first page, the ui state will have that page as not enabled.
    // So here we need to refresh the ui state so that our current nextPageState ui reflects
    // the context that we have in the store.
    if (nextPageState && nextPageState.ui.pageIndex == 0) {
      nextPageState = getStateFromSlug(
        nextPageState.pageSet,
        loaderContext,
        nextPageState.ui.page.slug,
        nextPageState.lastCompletedIndex,
      );
    }
    if (nextPageState === null) {
      pageFound = false;
      // this error will be no pages found
      break;
    }

    // exit the loop if we were at the end
    // - there's no point continuing to call nextPage since there are no more pages
    // exit the loop if our current page isEnabled
    // - we have an enabled page (that may or may not have a loader, but if it does that loader has been called)
    // exit the loop if we no longer have a loader
    // - nextPage returned a new page that does not have a loader so we can exit
    loader = getPageLoader(nextPageState.ui.page.slug);

    if (
      baseState.atEnd ||
      (currentSlug == nextPageState.ui.page.slug && nextPageState.ui.page.isEnabled) ||
      !loader
    ) {
      pageFound = true;
      break;
    }

    // Now that we have called this page's loader, remove it from always
    // return so that nextPage does not return the same page *unless* this
    // page is now enabled
    alwaysReturnPagesWithLoaders = alwaysReturnPagesWithLoaders.filter(
      (slug) => slug != nextPageState?.ui.page.slug,
    );

    // Hopefully we never see this error, but just in case let's not lock up the user's broswer
    limit--;
    if (limit == 0) {
      //TODO: this will send the user to the error page
      throw Error('Too many loaders called in a row, check flow logic!');
    }
  }

  return { flowState: nextPageState, pageFound };
};

const goToNextPage = async (_: Action, listenerApi: ListenerEffectAPI<RootState, AppDispatch>) => {
  const appState = listenerApi.getState();
  const { state: preSubmitState, context: preSubmitContext } = getFlowFromState(appState);

  if (preSubmitState.atEnd || preSubmitState?.ui.page?.exitPath) {
    if (preSubmitContext.inputContext.isInPartnershipExpansionDenial) {
      analytics.trackEvent('flow-state', 'change', 'partnership-denied');
    }
    // We have reached the end of the flow
    handleAtEnd(preSubmitContext, preSubmitState, listenerApi);
    return;
  }

  // Handle early denial from account page
  // TODO: move to accountPage exitPath
  const isExclusives = isInExclusiveOffersTreatment(appState);
  if (
    preSubmitState.ui.page.slug == 'account' &&
    preSubmitContext.inputContext.eligibleProducts.length == 0 &&
    !isExclusives
  ) {
    analytics.trackEvent('flow-state', 'change', 'denied');
    if (preSubmitContext.inputContext.inApp) {
      postDataToWebview({
        eventName: 'denied',
        payload: { denialReason: appState.sellerInput.denialReason },
      });
    } else {
      listenerApi.dispatch(
        push(
          sellerGlobalPagePath({
            token: preSubmitContext.inputContext.sellerInputUUID,
            page: 'unavailable',
          }),
        ),
      );
    }
  }

  let nextPageState = nextPage(preSubmitState, preSubmitContext, getPageLoaderSlugs());

  if (nextPageState === null) {
    // we are already on the last page, so handle end
    handleAtEnd(preSubmitContext, preSubmitState, listenerApi);
    return;
  }

  const loaderState = await handleLoaders(
    listenerApi,
    nextPage,
    nextPageState.ui.page.slug,
    preSubmitState,
  );

  if (loaderState) {
    const { flowState, pageFound } = loaderState;
    if (flowState) {
      nextPageState = flowState;
    }
    if (!pageFound) {
      // we had pages, but after running loaders an enabled page was not found
      handleAtEnd(preSubmitContext, preSubmitState, listenerApi);
      return;
    }
  }

  const { index, atStart, atEnd, ui } = nextPageState;

  if (preSubmitContext.inputContext.inApp) {
    postDataToWebview({ eventName: 'next', payload: { slug: ui.page.slug } });
  }

  listenerApi.dispatch(endPageLoading());

  listenerApi.dispatch(
    updateIndex({ index, atStart, atEnd, lastCompletedIndex: preSubmitState.index }),
  );
  listenerApi.dispatch(updateUi({ ...ui, pageLoading: false }));
  // post next page things
  listenerApi.dispatch(
    push(
      sellerFlowPagePath({
        token: preSubmitContext.inputContext.sellerInputUUID,
        slug: ui.page.slug,
        inApp: preSubmitContext.inputContext.inApp,
      }),
    ),
  );
};

const handleAtEnd = async (
  preSubmitContext: FlowContext,
  preSubmitState: FlowState,
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
) => {
  const appState = listenerApi.getState();

  analytics.trackEvent('flow-state', 'change', 'complete');
  if (preSubmitContext.inputContext.inApp) {
    postDataToWebview({ eventName: 'complete' });
  }

  const exitPath = preSubmitState.exitPath || preSubmitState?.ui.page?.exitPath;
  if (exitPath) {
    // need to handle end up flow ui updates here
    window.removeEventListener('beforeunload', showPrompt);
    listenerApi.dispatch(push(exitPath));
    return;
  }

  const isExclusives = isInExclusiveOffersTreatment(appState);

  const uuid = preSubmitContext.inputContext.sellerInputUUID;
  let defaultExitPath = '';
  const eligibleProducts = preSubmitContext.inputContext.eligibleProducts;
  if (eligibleProducts.includes('opendoor_direct')) {
    leadQualifyConversionEvent({
      sellerInputUUID: uuid,
      eligibleProducts,
      email: preSubmitContext.answers['seller.email'],
    });
    defaultExitPath = sellerGlobalPagePath({ token: uuid, page: 'last-step' });
  } else if (eligibleProducts.includes('agent_referral')) {
    defaultExitPath = sellerGlobalPagePath({ token: uuid, page: 'last-step' });
  } else if (isExclusives) {
    defaultExitPath = sellerGlobalPagePath({ token: uuid, page: 'last-step' });
  } else if (eligibleProducts.filter((prodOffering) => prodOffering !== 'reit').length === 0) {
    const reason = denialReason(appState);
    analytics.trackEvent('flow-state', 'change', 'denied');

    if (preSubmitContext.inputContext.inApp) {
      postDataToWebview({ eventName: 'denied', payload: { denialReason: reason } });
    } else {
      defaultExitPath = sellerGlobalPagePath({ token: uuid, page: 'unavailable' });
    }
  }

  if (!defaultExitPath) {
    // send to generic complete page
    defaultExitPath = sellerGlobalPagePath({ token: uuid, page: 'complete' });
  }
  window.removeEventListener('beforeunload', showPrompt);
  listenerApi.dispatch(push(defaultExitPath));
};

// these listeners are triggered on the account page
startAppListening({ actionCreator: uiSubmitAnswers, effect: submitAnswers });
startAppListening({ actionCreator: uiGoToNextPage, effect: goToNextPage });
// regular flow pages will always do a submit and go to next page as one call
startAppListening({
  actionCreator: submitAndGoToNextPage,
  effect: async (_, listenerApi) => {
    const startMS = Date.now();

    await submitAnswers(_, listenerApi);
    await goToNextPage(_, listenerApi);

    const state = listenerApi.getState();
    analytics.trackEvent('seller-flow', 'loading-time', 'next-slug', {
      value: Date.now() - startMS,
      currentSlug: state.ui.page?.slug ?? 'non-flow page',
    });
  },
});

// temporarly used for testing lib/flow to make sure we init the correct slug
const isFlowUrl =
  /^\/seller-lead\/[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}\/[\w\d-]+$/;

// This attempts to update the flow when the user clicks on back or uses the
// browser back/forward buttons
startAppListening({
  type: LOCATION_CHANGE,
  effect: async (_, listenerApi) => {
    const pathname = location.pathname;
    if (isFlowUrl.test(pathname)) {
      const currentSlug = location.pathname.split('/').pop() ?? '';

      const appState = listenerApi.getState();
      if (!appState.ui.flowInitialized) {
        // Page is still initializing, so bail out
        return;
      }

      const { state, context } = getFlowFromState(appState);

      if (currentSlug && state.ui.page.slug !== currentSlug) {
        let newState: FlowState;
        try {
          newState = getStateFromSlug(
            state.pageSet,
            context,
            currentSlug,
            state.lastCompletedIndex,
          );
        } catch (err: unknown) {
          // If we get this error something catastrophic has happened with lib/flow
          /* eslint-disable no-console */
          console.info(`Could not find page for ${currentSlug} while handling location change!`);
          throw err;
        }

        let getNextPage = previousPage;
        if (newState.ui.pageIndex > state.ui.pageIndex) {
          // our new page is forward in the flow, so if we have loaders use
          // nextPage to iterate forward into the flow
          getNextPage = nextPage;
        }

        const loaderState = await handleLoaders(
          listenerApi,
          getNextPage,
          newState.ui.page.slug,
          state,
        );

        if (loaderState) {
          const { flowState } = loaderState;
          if (flowState) {
            newState = flowState;
          }
          // handle null and pageFound == false as an error
        }

        const { index, atStart, atEnd, ui } = newState;

        listenerApi.dispatch(updateIndex({ index, atStart, atEnd }));
        listenerApi.dispatch(updateUi(ui));

        if (loaderState?.flowState && loaderState.flowState.ui.page.slug !== currentSlug) {
          // update url if we've gone back more than one page
          listenerApi.dispatch(
            push(
              sellerFlowPagePath({
                token: context.inputContext.sellerInputUUID,
                slug: loaderState.flowState.ui.page.slug,
                inApp: context.inputContext.inApp,
              }),
            ),
          );
        }
      }
    }
    // whatever path this is, we've already updated the latest state, or it's a
    // non-flow page, so do nothing
  },
});

// We like to do a bunch tracking updates after answers have been submitted
startAppListening({
  matcher: api.endpoints.updateAnswers.matchFulfilled,
  effect: ({ payload, meta }, listenerApi) => {
    const { completedSlug, submittedAnswers } = meta.arg.originalArgs;
    const sellerInput = payload.reception?.updateSellerInput?.sellerInput;

    // this copies the original functionality
    if (!sellerInput) {
      const errMsg = 'Seller input from UpdateAnswers response is undefined';
      Sentry.withScope((scope) => {
        scope.setExtras({ completedSlug, submittedAnswers });
        Sentry.captureException(errMsg);
      });

      // something really bad has happened, ideally we would ask the user to
      // retry but we do not have that ui. It's also possible that by going to
      // the next page this will correct itself, so for now we return
      // also this error has not been seen in the last 90 days
      // we should remove this soon after we ship the refactor (JD 5/26/23)
      return;
    }

    // convert raw gql values and update seller input
    const opendoorDirectDenialReason = getOpendoorDirectDenialReason(sellerInput);
    const channel = channelStringToEnum(sellerInput.channel);
    const denialCode = getOpendoorDirectDenialReason(sellerInput);
    const customerUuid = sellerInput.customerUuid;
    const denialReason = generateDenialReasonDisplayString({
      denialReason: opendoorDirectDenialReason,
      maxLotSqFt: sellerInput.opendoorDirectBuyboxThresholds?.maxLotSqFt,
      minYearBuilt: sellerInput.opendoorDirectBuyboxThresholds?.minYearBuilt,
      addressStreet: sellerInput.address?.street || '',
    });
    const lastCompletedSlug = sellerInput?.sellerFlowLastCompletedSlug;
    const isAddressAutoVerified =
      addressVerifiedStatusEnumToAddressVerifiedStringUnion(sellerInput.addressVerifiedStatus) ===
      'auto_verified';

    const leadState = getLeadState(
      sellerInput.addressVerifiedStatus,
      sellerInput.isListed,
      sellerInput.productOfferings,
      sellerInput.address,
    );
    const partnerships = sellerInput?.partnershipReferrals ?? [];
    const productOfferings = getProductOfferings(sellerInput) || [];

    const savedAnswers = JSON.parse(sellerInput.answersJson ?? '{}') as Record<string, SavedAnswer>;

    listenerApi.dispatch(
      updateSellerInputAfterUpdateAnswers({
        isAddressAutoVerified,
        channel,
        customerUuid,
        denialCode,
        denialReason,
        lastCompletedSlug: lastCompletedSlug ?? undefined,
        partnerships: partnerships ?? [],
        productOfferings,
        serverAnswers: savedAnswers,
        leadState,
        submittedAnswers,
      }),
    );
  },
});

// This initializes our flow_ng and ui slices once we've loaded the seller input
startAppListening({
  matcher: api.endpoints.getSellerInputs.matchFulfilled,
  effect: async (action, listenerApi) => {
    const payload = action.payload;
    let appState = listenerApi.getState();
    if (appState.flow_ng.pageSetName == '' && payload.reception?.getSellerInput?.sellerInput) {
      const {
        sellerFlowLastCompletedSlug,
        address,
        addressVerifiedStatus,
        answerPrefillsJson,
        answersJson,
        channel,
        customerUuid,
        isListed,
        partnershipReferrals,
        productOfferings,
        region,
        sellerFlowName: sellerInputSellerFlow,
        uuid,
        denialPartnerIdentifier,
        homeValue,
      } = payload.reception.getSellerInput.sellerInput;

      const sellerFlowName = getInitialSellerFlowName(sellerInputSellerFlow);

      appState = listenerApi.getState();
      const leadState = getLeadState(addressVerifiedStatus, isListed, productOfferings, address);
      const prefills = JSON.parse(answerPrefillsJson);
      const answers = JSON.parse(answersJson);

      const flowContext: FlowContext = {
        answers: {
          ...Object.keys(prefills).reduce((all, key) => {
            all[key] = prefills[key]['value'];
            return all;
          }, {}),
          ...answers,
        },
        inputContext: {
          answerPrefills: prefills,
          sellerInputUUID: uuid,
          channel,
          marketDisplayName: region,
          market: region,
          isInBwODMarket: isInBwODMarket(region),
          eligibleProducts: getEligibleProducts(leadState, productOfferings),
          partnerships: partnershipReferrals,
          homebuilderIdentifier: getHomeBuilderIdentifier(partnershipReferrals),
          orePartnerIdentifier: getOrePartnerIdentifier(partnershipReferrals),
          homeValuationEstimate: getHomeValuationEstimate(homeValue),
          isInPartnershipExpansionDenial: getIsInPartnershipExpansionDenial(
            productOfferings,
            denialPartnerIdentifier,
          ),
          hasHomebuilderSalesAssociate: getHasHomebuilderSalesAssociate(partnershipReferrals),
          inApp: isOpendoorAppEvoWebView, // need to check queryparam - see getInApp
          sellerFlowName,

          // this needs to be a combo of homebuilderIdentifier and seller
          // answers, so maybe it shouldn't be here..
          isHomebuilderLead: false,
          // this also depends on answers..
          // these could be helpers the flow lib has that are like isOpcityEligible(flowContext)
          //opcityEligible?: boolean;
        },
        customer: {
          // might change during app usage?
          customerUUID: customerUuid,
          // these depend on answers - so maybe don't belong here
          yourOrYourClients: 'your',
          yourOrYourClient: 'your',
        },
        featureFlags: appState.featureFlags,
        experimentFlags: appState.experimentFlags,
      };

      if (
        leadState !== 'denied' &&
        !isInExclusiveOffersTreatment(appState) &&
        !areAllProductsDenied(appState)
      ) {
        await loadExperiment(listenerApi, 'addressEntryConfirmation', true);
      }

      const { pageSet, name: pageSetName } = getPageSet(sellerFlowName, flowContext);
      const currentSlug = location.pathname.split('/').pop() ?? '';
      const isFlowPath = location.pathname.indexOf('/p/') == -1;
      let initialState = initFlowState(
        pageSet,
        flowContext,
        currentSlug,
        sellerFlowLastCompletedSlug,
      );
      const { index, atEnd, atStart, ui } = initialState;
      listenerApi.dispatch(
        updateFlow({
          index,
          atEnd,
          atStart,
          pageSetName: pageSetName,
          lastCompletedIndex: index,
          inputContext: flowContext.inputContext,
        }),
      );
      listenerApi.dispatch(updateUi(ui));

      //do page loading after we attempted to setup some initial state
      const loader = getPageLoader(ui.page.slug);
      let actualSlug = ui.page.slug;
      if (loader) {
        // load this page's loader and update our state so we have any feature flags and isEnabled is called with the correct data
        await Promise.all(loader.map((l) => l(listenerApi)));
        listenerApi.dispatch(updateUi(ui));
        const appState = listenerApi.getState();
        initialState = getFlowFromState(appState).state;

        if (initialState.ui.page.isEnabled == false) {
          // If we have A -> B -> C and we are on B, and B has a loader, then we
          // want to be able to call that loader to update our state and then ask
          // the flow what comes after page A. B may be enabled or disabled by
          // the loader, so the next page could be B or C
          //
          // To do this we need to pass in the previous ui state. At this point
          // in the code though we're starting from a slug passed in from the url
          // and so we do not have that previous state and instead we need
          // recreate it
          let prevState: FlowState | null;
          prevState = previousPage(initialState, flowContext);
          if (prevState === null) {
            // there is no previous state, so use an uninitialized one set to the
            // very beginning of the flow
            const { pageSet } = getPageSet(sellerFlowName, flowContext);
            prevState = createNewFlowState(pageSet, flowContext);
          }

          const { flowState: loaderState } = await handleLoaders(
            listenerApi,
            nextPage,
            ui.page.slug,
            prevState,
          );
          if (loaderState) {
            // we've intialized our page set but for some reason we have no pages enabled
            // there's nothing we can do here but handle the end of flow
            if (loaderState.atEnd && !loaderState.ui.page.isEnabled) {
              handleAtEnd(flowContext, loaderState, listenerApi);
            }

            const updatedFlowState = getFlowFromState(listenerApi.getState());

            // we had a loader that changed the state, so send all that to redux
            listenerApi.dispatch(
              updateFlow({
                index: loaderState.index,
                atEnd: loaderState.atEnd,
                atStart: loaderState.atStart,
                pageSetName: sellerFlowName,
                lastCompletedIndex: index,
                inputContext: updatedFlowState.context.inputContext,
              }),
            );

            listenerApi.dispatch(updateUi(loaderState.ui));

            // update url if needed
            actualSlug = loaderState.ui.page.slug;
          }
        } else {
          // we've intialized our page set but for some reason we have no pages enabled
          // there's nothing we can do here but handle the end of flow
          // This probably should not happen, but potentially can happen in the
          // partner flow if a seller input is created with a ore or homebuilder
          // identifier
          if (initialState.atEnd && !initialState.ui.page.isEnabled) {
            handleAtEnd(flowContext, initialState, listenerApi);
          }
        }
      }

      // if we ended up showing a page that is different than our original slug
      // we need to update the url to that
      if (isFlowPath && actualSlug !== currentSlug) {
        listenerApi.dispatch(
          replace(
            sellerFlowPagePath({
              token: flowContext.inputContext.sellerInputUUID,
              slug: actualSlug,
              inApp: flowContext.inputContext.inApp,
            }),
          ),
        );
      }

      listenerApi.dispatch(flowInitialized());

      // If we're at the start of the flow, be sure to handle any additional starting pages
      // such as missing unit number, etc
      if (initialState.atStart && isFlowPath) {
        handleAtStart(action, listenerApi);
        return;
      }
    }
  },
});

startAppListening({
  matcher: api.endpoints.getVariantQuery.matchFulfilled,
  effect: ({ payload, meta }, listenerApi) => {
    const shortBoardingFlag: ExperimentFlag = 'shortboarding';
    if (meta.arg.originalArgs.experimentFlag === shortBoardingFlag) {
      if (payload.variant.getVariant?.variant?.value === 'treatment') {
        // for shortboarding we do not want to generate an offer until later in
        // the flow. So here we set the flag to disable offer generation on "required answers filled"
        const appState = listenerApi.getState();
        listenerApi.dispatch(
          api.endpoints.updateAnswers.initiate({
            uuid: appState.sellerInput.leadToken,
            completedSlug: appState.ui.page?.slug ?? '',
            submittedAnswers: { 'seller.qualify_on_required_answers': false },
          }),
        );
      }
    }
  },
});
